<script setup lang="ts">
/**
 * Les mouvements du solde de SMS (écran 9, S:5841), en lecture.
 *
 * Selon le geste `sms_voir_mouvements` de la matrice :
 *  • absent (un employé, un lecteur) : rien, même si l'écran recevait des
 *    mouvements ;
 *  • inactif (le Basique) : le titre, l'étiquette « Avancé » et la raison ;
 *  • actif : le tableau, seulement si la route a servi `mouvements` ; une
 *    liste vide servie le dit. Un sens que l'écran ne connaît pas se dit
 *    « Mouvement non reconnu », un nombre absent « — », jamais 0.
 */
// ================================
// IMPORTS
// ================================
import { useI18n } from "vue-i18n"
import type { Acces } from "@modules/alarmes-v2/acces/acces"
import { formaterDate } from "@modules/alarmes-v2/utils/mesure"
import type { AlarmesV2MouvementSms } from "@modules/alarmes-v2/types/alarmes-v2"

defineProps<{
    /** Le verdict de `sms_voir_mouvements`. */
    acces: Acces
    /** Les mouvements servis ; `undefined` quand la route ne les a pas servis. */
    mouvements?: AlarmesV2MouvementSms[]
}>()

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n()

/** Les sens que l'écran sait dire. */
const SENS_CONNUS = ["credit", "debit", "regularisation"]

/**
 * Le sens d'un mouvement en toutes lettres.
 * @param sens - le sens servi
 */
function libelleSens(sens: string | null): string {
    return SENS_CONNUS.includes(sens ?? "")
        ? t(`alarmesV2.option.mouvements.sens.${sens}`)
        : t("alarmesV2.option.mouvements.sens.inconnu")
}

/**
 * Un nombre servi, ou « — » s'il manque.
 * @param n - le nombre servi
 */
function nombre(n: number | null): string {
    return typeof n === "number" ? n.toLocaleString("fr-FR") : "—"
}
</script>

<template>
    <!-- ================================ -->
    <!-- COMPOSANT PRINCIPAL -->
    <!-- ================================ -->
    <section
        v-if="acces.affichage === 'inactif' || (acces.ok && mouvements)"
        class="rounded-lg border border-surface-200 bg-surface-0 px-4 py-3"
        data-test="mouvements-sms"
    >
        <h2 class="text-base font-semibold text-surface-900">
            <LigneNiveau
                v-if="!acces.ok"
                :texte="t('alarmesV2.option.mouvements.titre')"
                :avance="acces.etiquetteAvance"
                :raison="acces.raison"
            />
            <template v-else>{{
                t("alarmesV2.option.mouvements.titre")
            }}</template>
        </h2>
        <template v-if="acces.ok && mouvements">
            <p
                v-if="mouvements.length === 0"
                class="mt-2 text-sm text-surface-600"
                data-test="aucun-mouvement"
            >
                {{ t("alarmesV2.option.mouvements.aucun") }}
            </p>
            <div v-else class="mt-2 overflow-x-auto">
                <table class="w-full text-left text-sm">
                    <thead class="text-xs text-surface-600">
                        <tr>
                            <th scope="col" class="py-2 pr-4 font-medium">
                                {{
                                    t(
                                        "alarmesV2.option.mouvements.colonnes.date"
                                    )
                                }}
                            </th>
                            <th scope="col" class="py-2 pr-4 font-medium">
                                {{
                                    t(
                                        "alarmesV2.option.mouvements.colonnes.mouvement"
                                    )
                                }}
                            </th>
                            <th scope="col" class="py-2 pr-4 font-medium">
                                {{
                                    t(
                                        "alarmesV2.option.mouvements.colonnes.sms"
                                    )
                                }}
                            </th>
                            <th scope="col" class="py-2 font-medium">
                                {{
                                    t(
                                        "alarmesV2.option.mouvements.colonnes.soldeApres"
                                    )
                                }}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="(m, i) in mouvements"
                            :key="i"
                            class="border-t border-surface-100"
                            data-test="mouvement"
                        >
                            <td class="py-2 pr-4">
                                {{ formaterDate(m.le) ?? "—" }}
                            </td>
                            <td class="py-2 pr-4" data-test="sens">
                                {{ libelleSens(m.sens) }}
                            </td>
                            <td class="py-2 pr-4" data-test="segments">
                                {{ nombre(m.segments) }}
                            </td>
                            <td class="py-2" data-test="solde-apres">
                                {{ nombre(m.solde_apres) }}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </template>
    </section>
</template>
